import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'

import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { CommissionPlan } from '@model/MultiTierDto'

interface CommissionPlansSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  summaryData: CommissionPlan[]
  isLoading: boolean
}

export const useCommissionPlansSummary = (): CommissionPlansSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const queryClient = useQueryClient()

  const introducingBrokerId = account?.clientIntroducingBroker?.id
  const queryKey = queryKeys.ib.commissionPlansSummary(introducingBrokerId)

  const { data: summaryData, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.getIBCommissionPlansSummary(introducingBrokerId!),
    enabled: !!introducingBrokerId,
  })

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return (
      summaryData?.commissionPlans.map((plan) => ({
        title: plan.tradingAccountType.name,
        value: plan.amount,
      })) ?? []
    )
  }, [summaryData])

  return {
    summaryItems,
    summaryData: summaryData?.commissionPlans ?? [],
    forceRefresh: () => queryClient.invalidateQueries({ queryKey }),
    isLoading,
  }
}
